import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { IProperty, IPropertySectionProps } from '../../../types';
import { EDataTableType, EDisabledProperties, EPropertyType } from '../../data/Constants';
import { CustomButton } from '../CustomButton';
import { getDefaultIcon, HistoryButton } from '../IconTypes';
import { DynamicInputControl } from './DynamicInputControl';

/**
 * Get the default value based on property type
 * @param { IProperty } property property object
 * @returns { string | null } return the default value
 */
function getPropertyDefault(property: IProperty): string | null {
  if (property.default === null) {
    return null;
  }

  if (property.type === EPropertyType.Enum) {
    return property.default;
  }

  return property.default.code;
}

/**
 *Check the association  and return value
 * @param { IPropertyAssociation } property property associations
 * @param { [key: string]: string | null } association the default value for the property
 * @returns { string }  return the current property association
 */

function getAssociation(
  property: IProperty,
  association: { [key: string]: string | null }
): string | null {
  const defaultValue = getPropertyDefault(property);
  return association?.[property.code] ?? defaultValue;
}

/**
 * Returns a section for all Properties with required form controls
 * @param {IPropertySectionProps} props Property section props
 * @returns {React.JSX.Element} A section with passed properties as form controls
 */

export const PropertiesSection = (props: IPropertySectionProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <fieldset className="inputFieldset w-100 h-100">
      <legend className="inputLegend">
        {props.label}
        <Tooltip title={t('tooltip.edit')}>
          <span>
            <IconButton
              className={props.editProperties ? 'action-icon-disabled' : 'action-icon'}
              onClick={(): void => props.setEditProperties(true)}
              disabled={props.editProperties}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      </legend>
      <Box className="flow">
        {props.properties.length === 0 ? (
          t('messages.pdm.noPropertiesFound')
        ) : (
          <>
            {props.properties.map((property) => {
              // Check if this property should remain disabled (not editable) in edit mode
              // EDisabledProperties is a map of material types to arrays of property codes
              const isPropertyDisabled =
                EDisabledProperties[
                  props.material.type as keyof typeof EDisabledProperties
                ]?.includes(property.code) ?? false;

              // Fields are enabled for editing when editProperties is true
              // Exception: Properties in the disabled list remain read-only (disabled=false)
              const disabled = props.editProperties && !isPropertyDisabled;

              return (
                <Grid
                  container
                  key={property.code}
                  spacing={2}
                  className={
                    property.type === EPropertyType.Boolean
                      ? 'pr-1 align-center'
                      : 'pt-1 pr-1 align-center'
                  }
                >
                  <Grid size={10}>
                    <DynamicInputControl
                      property={property}
                      propValue={getAssociation(property, props.associations)}
                      handleInputChange={props.handleInputChange}
                      fieldsEnabled={disabled}
                    />
                  </Grid>
                  <Grid size={1} className="d-flex justify-center">
                    {getDefaultIcon(props.associations, property.default, property.code, t)}
                  </Grid>
                  <Grid size={1} className="d-flex justify-center">
                    <Tooltip title={t('labels.history')}>
                      <span>
                        <HistoryButton
                          showPropertyHistoryDialog={(): void =>
                            props.showPropertyHistoryDialog(
                              EDataTableType.PropertyHistory,
                              true,
                              property
                            )
                          }
                          hideLabel
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              );
            })}
          </>
        )}
      </Box>
      <div className="action-button">
        <CustomButton
          label={t('button.save')}
          variant="contained"
          disabled={
            !props.editProperties || !props.isDataUpdated || (props.isSaveDisabled ?? false)
          }
          type={props.propertyVisibility}
          handleAction={props.handleSave}
        />
        <CustomButton
          label={t('button.cancel')}
          variant="outlined"
          disabled={!props.editProperties}
          type={props.propertyVisibility}
          handleAction={props.handleCancel}
        />
      </div>
    </fieldset>
  );
};
